import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../../contexts';
import { getDate } from '../../../helpers/dateFormat';
import { formatDuration } from '../../../helpers/formatDuration';
import {
  ICalendar,
  ICreateAppointment,
  IBusinessView,
  IBusinessPublicView,
  IBusinessEmployeeView,
  IServiceView,
  IClientView,
  appointmentService,
  clientService,
  feedback,
  businessEmployeeService,
  IBusinessEmployeeServiceView,
} from '../../../services';
import { Api } from '../../../services/api/axios-config';
import { setCsrfRequestToken } from '../../../services/api/axios-config/interceptors';
import { Calendar } from '../calendar/Calendar';
import { Button } from '../../MUI/button/Button';
import { CreateClientModal } from '../createClientModal/CreateClientModal';
import { Autocomplete, TextField } from '@mui/material';
import { EmployeeAvatarSelector } from '../employeeAvatarSelector/EmployeeAvatarSelector';

interface IScheduleModalProps {
  open: boolean;
  onClose: () => void;
  business: IBusinessView | IBusinessPublicView;
  service: IServiceView;
  viaScheduler?: boolean;
  walkIn?: boolean;
  blockEmployee?: boolean;
  initialEmployeeId?: number;
  /** Cliente pré-selecionado (vindo do CreateAppointmentForm, via scheduler) */
  preselectedClient?: IClientView | null;
  /** ID do usuário logado (para agendamento normal) */
  userId?: number;
  onSuccess: () => void;
  /** Callback para expor estado de submit ao pai (opcional) */
  onReady?: (api: { isLoading: boolean; canSubmit: boolean; submit: () => void }) => void;
}

export const ScheduleModal: React.FC<IScheduleModalProps> = ({
  open,
  onClose,
  business,
  service,
  viaScheduler,
  walkIn,
  blockEmployee,
  initialEmployeeId,
  preselectedClient,
  userId: propUserId,
  onSuccess,
  onReady,
}) => {
  const { userId: contextUserId } = useUserContext();
  const userId = propUserId ?? contextUserId;

  const [selectedEmployee, setSelectedEmployee] = useState<IBusinessEmployeeView>();
  const [monthDay, setMonthDay] = useState<ICalendar>();
  const [selectedTime, setSelectedTime] = useState<string>();

  // Client search state (viaScheduler)
  const [customer, setCustomer] = useState<string>();
  const [selectedClient, setSelectedClient] = useState<IClientView | null>(null);

  // Use preselected client from parent when available
  const clientForSubmit = preselectedClient ?? selectedClient;
  const [clientSearch, setClientSearch] = useState('');
  const [clientModalOpen, setClientModalOpen] = useState(false);

  const [preApprovalDialogOpen, setPreApprovalDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Employees that offer this service
  const employees = useMemo(() => {
    const allEmps = 'employees' in business ? (business as IBusinessView).employees : undefined;
    return (allEmps || []).filter(e =>
      e.isActive &&
      e.businessEmployeeServices?.some((bes: IBusinessEmployeeServiceView) => bes.serviceId === service.id)
    );
  }, [business, service]);

  // Pre-select first employee (or initial)
  useEffect(() => {
    if (open && employees.length > 0) {
      if (initialEmployeeId) {
        const emp = employees.find(e => e.id === initialEmployeeId) || employees[0];
        setSelectedEmployee(emp);
      } else {
        setSelectedEmployee(employees[0]);
      }
      // Reset time/date when modal opens
      setMonthDay(undefined);
      setSelectedTime(undefined);
      setSelectedClient(null);
      setCustomer('');
      setClientSearch('');
    }
  }, [open, employees, initialEmployeeId]);

  const serviceDuration = useCallback((emp: IBusinessEmployeeView) => {
    return emp.businessEmployeeServices?.find(s => s.serviceId === service.id)?.serviceDuration;
  }, [service]);

  const employeeServiceDuration = useMemo(
    () => selectedEmployee && serviceDuration(selectedEmployee),
    [selectedEmployee, serviceDuration]
  );

  // Available spots
  const { data: availableSpots, isFetching: isFetchingAvailableSpots } = useQuery({
    queryKey: ['availableSpots', selectedEmployee?.id, monthDay, employeeServiceDuration],
    queryFn: () => {
      if (selectedEmployee && monthDay && employeeServiceDuration) {
        return businessEmployeeService.getAvailableSpots(
          selectedEmployee.id,
          monthDay.monthDay,
          monthDay.month,
          employeeServiceDuration,
          walkIn || undefined
        );
      }
      return undefined;
    },
    enabled: !!selectedEmployee && !!monthDay && !!employeeServiceDuration && open,
    staleTime: 30_000,
  });

  // Client search
  const { data: clients, isFetching: isFetchingClients } = useQuery({
    queryKey: ['clients', business.id, clientSearch],
    queryFn: () => clientService.searchClients(business.id, clientSearch || undefined),
    enabled: !!viaScheduler && clientSearch.length >= 0 && open,
  });

  // Mutation
  const { mutate: createAppointment, isLoading: isLoadingCreate } = useMutation(
    async (appt: ICreateAppointment) => {
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort
      }
      return appointmentService.create(appt);
    },
    {
      onSuccess: () => {
        feedback('Agendamento criado', 'success');
        queryClient.invalidateQueries(['availableSpots']);
        onSuccess();
        onClose();
      },
      onError: (error: Error) => feedback(String(error), 'error'),
    }
  );

  const submitAppointment = useCallback(() => {
    if (!monthDay || !selectedTime || !selectedEmployee) return;

    const [hour, minutes] = selectedTime.split(':').map(Number);
    const date = getDate(monthDay.month - 1, monthDay.monthDay, hour, minutes);

    if (date && business.id && selectedEmployee.id && service.id) {
      const submitData: ICreateAppointment = {
        date,
        businessId: business.id,
        businessEmployeeId: selectedEmployee.id,
        services: [{ serviceId: service.id }],
        products: [],
        userId: viaScheduler ? (clientForSubmit?.userId || undefined) : userId,
        customer: viaScheduler
          ? (clientForSubmit ? (clientForSubmit.userId ? undefined : clientForSubmit.name) : customer)
          : undefined,
        isWalkIn: walkIn || undefined,
      };

      createAppointment(submitData);
    }
  }, [selectedEmployee, service, monthDay, selectedTime, walkIn, viaScheduler, selectedClient, customer, userId, createAppointment, business]);

  const handleSubmit = useCallback(() => {
    if (!monthDay || !selectedTime) return;

    if (service.requiresPreApproval) {
      setPreApprovalDialogOpen(true);
      return;
    }

    submitAppointment();
  }, [service, monthDay, selectedTime, submitAppointment]);

  // Expose state to parent
  const canSubmit = !!selectedTime;
  useEffect(() => {
    onReady?.({ isLoading: isLoadingCreate, canSubmit, submit: handleSubmit });
  }, [isLoadingCreate, canSubmit, handleSubmit, onReady]);

  const handleChangeEmployee = (emp: IBusinessEmployeeView | undefined) => {
    setSelectedEmployee(emp);
    setSelectedTime(undefined);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>
          Agendar: {service.description}
        </DialogTitle>

        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} sx={{ mt: 1 }}>
            <EmployeeAvatarSelector
              label='Profissional'
              employees={employees}
              value={selectedEmployee?.id}
              onChange={(id) => handleChangeEmployee(id ? employees.find(e => e.id === id) : undefined)}
              disabled={blockEmployee}
              renderExtra={(emp) => {
                const fullEmp = employees.find(e => e.id === emp.id);
                if (!fullEmp) return null;
                const duration = serviceDuration(fullEmp);
                return duration ? (
                  <Typography variant='caption' display='block' color='text.disabled' align='center'>
                    {formatDuration(duration)}
                  </Typography>
                ) : null;
              }}
            />

            {/* Client Search (viaScheduler) — only when no preselected client from parent */}
            {viaScheduler && !preselectedClient && (
              <Box display='flex' gap={1} alignItems='flex-start'>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={clients || []}
                  getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.name}${opt.phone ? ` - ${opt.phone}` : ''}`}
                  value={selectedClient}
                  onChange={(_, value) => {
                    if (value && typeof value !== 'string') {
                      setSelectedClient(value);
                      setCustomer('');
                    } else {
                      setSelectedClient(null);
                      if (typeof value === 'string') {
                        setCustomer(value);
                      }
                    }
                  }}
                  onInputChange={(_, value) => {
                    setClientSearch(value);
                    if (!selectedClient || (selectedClient && value !== selectedClient.name)) {
                      setCustomer(value);
                    }
                  }}
                  freeSolo
                  loading={isFetchingClients}
                  loadingText='Buscando clientes...'
                  noOptionsText='Nenhum cliente encontrado'
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Cliente'
                      required
                      variant='outlined'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isFetchingClients ? <LinearProgress sx={{ width: 20 }} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => setClientModalOpen(true)}
                  color='primary'
                  title='Novo cliente'
                  sx={{ mt: 0.5 }}
                >
                  <PersonAddIcon />
                </IconButton>
              </Box>
            )}

            {/* Calendar */}
            <Calendar monthDay={monthDay} setMonthDay={setMonthDay} />

            {/* Time Slots */}
            {availableSpots && availableSpots.length > 0 ? (
              <>
                <Typography variant='h6'>Horários disponíveis:</Typography>
                <Box display='flex' gap={2} flexWrap='wrap'>
                  {availableSpots.map((item) => (
                    <Button
                      key={item}
                      label={item}
                      onClick={() => setSelectedTime(item)}
                      variant={item === selectedTime ? 'contained' : 'outlined'}
                      color='primary'
                      size='large'
                      borderRounded={false}
                      data-testid={`time-slot-${item.replace(':', '-')}`}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                {isFetchingAvailableSpots ? (
                  <>
                    <Typography variant='h6'>Horários disponíveis:</Typography>
                    <LinearProgress />
                  </>
                ) : monthDay ? (
                  <Typography variant='h6'>Não há horários disponíveis para este dia...</Typography>
                ) : null}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button label='Cancelar' variant='outlined' onClick={onClose} />
          <Button
            label='Agendar'
            variant='contained'
            onClick={handleSubmit}
            isLoading={isLoadingCreate}
            disabled={!canSubmit}
            data-testid='btn-submit-schedule'
          />
        </DialogActions>
      </Dialog>

      {/* Create Client Modal */}
      {business.id && (
        <CreateClientModal
          open={clientModalOpen}
          businessId={business.id}
          onClose={() => setClientModalOpen(false)}
        />
      )}

      {/* Pre-approval dialog */}
      <Dialog
        open={preApprovalDialogOpen}
        onClose={() => setPreApprovalDialogOpen(false)}
      >
        <DialogTitle>⚠️ Confirmação necessária</DialogTitle>
        <DialogContent>
          <Typography>
            O serviço <strong>{service.description}</strong> requer
            confirmação do profissional. Seu agendamento será enviado e um
            profissional confirmará o horário em breve.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreApprovalDialogOpen(false)} variant='outlined'>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setPreApprovalDialogOpen(false);
              submitAppointment();
            }}
            variant='contained'
          >
            Entendi, agendar mesmo assim
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
